import { useEffect, useState } from 'react';
import {
  subscribeToQueryFileExternalChange,
  subscribeToQueryFileExternalDelete,
  unwatchSqlFile,
  watchSqlFile,
} from '../services/queryFilesApi';
import { toast } from '../../../../components/ui/toaster';
import { describeOversizedExternalChange } from '../../../../types/editorTextLimits';

export type ExternalFileChange =
  | { kind: 'changed'; newContent: string }
  | { kind: 'deleted' };

/**
 * Watches the given file while mounted. When another process modifies (or
 * deletes) the file, `pendingChange` is set — the caller is responsible for
 * prompting the user and calling `clearPending` once handled.
 *
 * Own-writes from open/save IPC are suppressed in the main process, so only
 * true external edits surface here.
 */
export function useExternalFileChange(tabId: string, filePath: string | undefined) {
  const [pendingChange, setPendingChange] = useState<ExternalFileChange | null>(null);

  useEffect(() => {
    const activePath = typeof filePath === 'string' && filePath.trim().length > 0 ? filePath : null;
    if (!activePath) return;

    setPendingChange(null);
    void watchSqlFile({ filePath: activePath, ownerId: tabId });

    const unsubChange = subscribeToQueryFileExternalChange((event) => {
      if (event.filePath !== activePath) return;
      if (event.tooLarge) {
        // Nothing to reload: the file cannot be loaded into the editor any
        // more — and an earlier, smaller snapshot still queued for "Reload"
        // is obsolete now, so drop it.
        setPendingChange(null);
        const fileName = activePath.split(/[\\/]/).pop() ?? activePath;
        toast.error({ title: 'File changed on disk', description: describeOversizedExternalChange(fileName, event.sizeBytes ?? 0) });
        return;
      }
      setPendingChange({ kind: 'changed', newContent: event.newContent });
    });
    const unsubDelete = subscribeToQueryFileExternalDelete((event) => {
      if (event.filePath !== activePath) return;
      setPendingChange({ kind: 'deleted' });
    });

    return () => {
      unsubChange();
      unsubDelete();
      void unwatchSqlFile({ filePath: activePath, ownerId: tabId });
    };
  }, [tabId, filePath]);

  return {
    pendingChange,
    clearPending: () => setPendingChange(null),
  };
}
